import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { History, Search, Filter, Eye, CheckCircle, XCircle, Clock, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const esitoColors = {
  successo: 'bg-green-100 text-green-800',
  errore: 'bg-red-100 text-red-800',
  timeout: 'bg-yellow-100 text-yellow-800'
};

const sezioni = [
  { value: 'auth', label: 'Autenticazione' },
  { value: 'volontari', label: 'Volontari' },
  { value: 'eventi', label: 'Eventi' },
  { value: 'presenze', label: 'Presenze' },
  { value: 'coperture', label: 'Coperture' },
  { value: 'documenti', label: 'Documenti' },
  { value: 'ente', label: 'Ente' },
  { value: 'reports', label: 'Report' }
];

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sezioneFilter, setSezioneFilter] = useState('all');
  const [esitoFilter, setEsitoFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [sezioneFilter, esitoFilter]);

  const fetchLogs = async () => {
    try {
      let url = `${API_URL}/api/logs?limit=100`;
      if (sezioneFilter && sezioneFilter !== 'all') {
        url += `&sezione=${sezioneFilter}`;
      }
      if (esitoFilter && esitoFilter !== 'all') {
        url += `&esito=${esitoFilter}`;
      }

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        setLogs(await response.json());
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Errore nel caricamento dei log');
    } finally {
      setLoading(false);
    }
  };

  const viewLogDetail = async (log) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      return new Date(timestamp).toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  const getEsitoIcon = (esito) => {
    switch (esito) {
      case 'successo':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'errore':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'timeout':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Log Operazioni</h1>
          <p className="page-subtitle">Registro delle operazioni e audit trail</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={sezioneFilter} onValueChange={setSezioneFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-sezione">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Tutte le sezioni" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le sezioni</SelectItem>
                {sezioni.map(sez => (
                  <SelectItem key={sez.value} value={sez.value}>{sez.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={esitoFilter} onValueChange={setEsitoFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-esito">
                <SelectValue placeholder="Tutti gli esiti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli esiti</SelectItem>
                <SelectItem value="successo">Successo</SelectItem>
                <SelectItem value="errore">Errore</SelectItem>
                <SelectItem value="timeout">Timeout</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="w-5 h-5 text-[#0055BB]" />
            Registro Operazioni
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="empty-state py-16">
              <History className="empty-state-icon" />
              <p className="empty-state-title">Nessun log trovato</p>
              <p className="empty-state-text">
                Le operazioni verranno registrate qui automaticamente
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Ora</TableHead>
                    <TableHead>Sezione</TableHead>
                    <TableHead>Operazione</TableHead>
                    <TableHead className="hidden md:table-cell">Utente</TableHead>
                    <TableHead>Esito</TableHead>
                    <TableHead className="w-16">Dettagli</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.log_id} data-testid={`log-row-${log.log_id}`}>
                      <TableCell className="font-mono text-sm">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {log.sezione}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize">
                          {log.operazione?.replace(/_/g, ' ')}
                        </span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {log.user_name}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getEsitoIcon(log.esito)}
                          <Badge className={esitoColors[log.esito] || 'bg-slate-100 text-slate-800'}>
                            {log.esito?.charAt(0).toUpperCase() + log.esito?.slice(1)}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewLogDetail(log)}
                          data-testid={`view-log-${log.log_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Dettaglio Operazione</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">ID Log</p>
                  <p className="font-mono text-sm">{selectedLog.log_id}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Timestamp</p>
                  <p className="font-mono text-sm">{formatTimestamp(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Sezione</p>
                  <Badge variant="outline" className="capitalize mt-1">
                    {selectedLog.sezione}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Operazione</p>
                  <p className="capitalize">{selectedLog.operazione?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Utente</p>
                  <p>{selectedLog.user_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Ruolo</p>
                  <p className="capitalize">{selectedLog.user_role?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Esito</p>
                  <Badge className={esitoColors[selectedLog.esito] || 'bg-slate-100'}>
                    {selectedLog.esito}
                  </Badge>
                </div>
                {selectedLog.durata_ms && (
                  <div>
                    <p className="text-sm text-slate-500">Durata</p>
                    <p>{selectedLog.durata_ms} ms</p>
                  </div>
                )}
              </div>
              {selectedLog.record_id && (
                <div>
                  <p className="text-sm text-slate-500">Record Coinvolto</p>
                  <p className="font-mono text-sm">
                    {selectedLog.record_tipo}: {selectedLog.record_id}
                  </p>
                </div>
              )}
              {selectedLog.dettagli && (
                <div>
                  <p className="text-sm text-slate-500">Dettagli</p>
                  <p className="text-sm bg-slate-50 p-2 rounded mt-1">{selectedLog.dettagli}</p>
                </div>
              )}
              {selectedLog.ip_address && (
                <div>
                  <p className="text-sm text-slate-500">IP Address</p>
                  <p className="font-mono text-sm">{selectedLog.ip_address}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Summary */}
      {logs.length > 0 && (
        <div className="text-sm text-slate-500 text-center">
          Visualizzati {logs.length} log più recenti
        </div>
      )}
    </div>
  );
}
